<template>
  <div style="overflow-x: auto">
    <div
      v-if="!defaultLegend"
      :id="legendKey"
      style="display: flex; justify-content: center; height: 41px"
    />
    <div :style="canvasStyle" style="position: relative; height: 300px; width: 100%">
      <line-chart :data="chartData" :options="chartOptions" :plugins="plugins" />
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { format, parseISO, min, max, eachDayOfInterval } from 'date-fns'
import { Line as LineChart } from 'vue-chartjs'
import { type Chart as ChartJS, type ChartData, type ChartOptions, Filler } from 'chart.js'

import { formatMoney } from '@/utils'

// represents a timeseries range
export interface RangeArea {
  legendLabel: string
  labels: [string, string] // eg: 10% - 90%, min - max
  history: {
    date: string
    value: [number, number]
  }[]
  color: string
}

export interface Line {
  label: string // a property name, or avg
  history: {
    date: string
    value: number
  }[]
  color: string
}

export type Value = RangeArea | Line

function instanceOfLine(object: Value): object is Line {
  return 'label' in object
}

const yAxisLimits = (values: number[], margin = 0.01) => {
  // compute the max as max(values) + margin*range rounded to the nearest 10^x

  const vals = values.filter((v) => v !== null)
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const range = max - min

  const digits = Math.pow(10, Math.floor(Math.log10(range)) - 1)

  const limitMin = Math.max(min - margin * range, 0)
  const limitMax = max + margin * range

  const roundedMin = Math.floor(limitMin / digits) * digits
  const roundedMax = Math.ceil(limitMax / digits) * digits

  if (roundedMin === roundedMax) {
    return [roundedMin - digits, roundedMax + digits]
  }

  return [roundedMin, roundedMax]
}

const getOrCreateLegendList = (chart: ChartJS, id: string) => {
  const legendContainer = document.getElementById(id)

  if (!legendContainer) {
    return null
  }

  let listContainer = legendContainer.querySelector('ul')

  if (!listContainer) {
    listContainer = document.createElement('ul')
    listContainer.style.display = 'flex'
    listContainer.style.flexDirection = 'row'
    listContainer.style.margin = '0px'
    listContainer.style.padding = '0px'

    legendContainer.appendChild(listContainer)
  }

  return listContainer
}

export default {
  name: 'PropertyCompareHistoryChart',
  components: {
    LineChart
  },
  props: {
    label: {
      type: String,
      required: true,
      description: 'The name of the quantity we are graphing, displayed on hover'
    },
    history: {
      type: Object as PropType<Value[]>,
      required: true
    },
    canvasStyle: {
      type: String,
      default: null
    },
    computeAggregates: {
      type: Boolean,
      default: true
    },
    areaAnnotated: {
      type: Object as () => { from: string; to: string } | null,
      description: 'Annotate the area between two dates',
      default: null
    },
    formatFnc: {
      type: Function,
      default: (value: number | null) => formatMoney(value)
    },
    defaultLegend: {
      type: Boolean,
      default: false
    },
    spanGaps: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  computed: {
    legendKey() {
      // return a unique key for the legend to force a redraw
      const now = new Date()
      return `${now.getTime()}`
    },
    plugins() {
      const plugins = [Filler]
      if (!this.defaultLegend) {
        plugins.push(this.htmlLegendPlugin)
      }
      return plugins
    },

    htmlLegendPlugin() {
      // after Update, remove the old legend and create a new one
      // such that we show ranges as a single legend item (append the two labels together)

      const drawLegend = (
        chart: ChartJS,
        _: unknown,
        options: { containerID: string; history: Value[] }
      ) => {
        const ul = getOrCreateLegendList(chart, options.containerID)
        if (ul && options.history?.length > 0) {
          ul.innerHTML = ''

          // Create container for legend items
          const container = document.createElement('div')
          container.style.display = 'flex'
          container.style.flexDirection = 'column'
          container.style.alignItems = 'center'
          container.style.width = '100%'
          ul.appendChild(container)

          // Create rows
          const row1 = document.createElement('div')
          row1.style.display = 'flex'
          row1.style.flexDirection = 'row'
          row1.style.flexWrap = 'nowrap'
          container.appendChild(row1)

          let row2: HTMLDivElement | undefined = undefined
          if (options.history.length > 8) {
            row2 = document.createElement('div')
            row2.style.display = 'flex'
            row2.style.flexDirection = 'row'
            row2.style.flexWrap = 'nowrap'
            container.appendChild(row2)
          }

          // Truncate history if more than 16 items
          const historyToShow = options.history.slice(0, 16)

          historyToShow.forEach((h: Value, index: number) => {
            const li = document.createElement('li')
            li.style.alignItems = 'center'
            li.style.display = 'flex'
            li.style.flexDirection = 'row'
            li.style.margin = '0px 2px 2px 2px'
            li.style.padding = '0px 5px 5px 5px'

            const key = document.createElement('span')
            key.style.backgroundColor = h.color
            key.style.borderWidth = '2px'
            key.style.display = 'block'
            key.style.height = '10px'
            key.style.marginRight = '5px'
            key.style.width = '10px'

            li.appendChild(key)

            const value = document.createElement('span')
            value.style.margin = '0px'
            value.style.padding = '0px'
            value.textContent = !instanceOfLine(h) ? h.legendLabel : h.label

            li.appendChild(value)

            // Add to first or second row based on index
            if (index < 8) {
              row1.appendChild(li)
            } else {
              row2?.appendChild(li)
            }
          })

          if (options.history.length > 16) {
            const more = document.createElement('li')
            more.style.alignItems = 'center'
            more.style.display = 'flex'
            more.style.margin = '5px'
            more.style.padding = '5px'
            more.textContent = `+${options.history.length - 16} more`
            row2?.appendChild(more)
          }

          if (this.spanGaps) {
            const li = document.createElement('li')
            li.style.alignItems = 'center'
            li.style.display = 'flex'
            li.style.flexDirection = 'row'
            li.style.margin = '0px 2px 2px 2px'
            li.style.padding = '0px 5px 5px 5px'

            const key = document.createElement('span')
            key.style.borderStyle = 'dotted'
            key.style.borderColor = this.$q.dark.isActive
              ? 'rgb(255,255,255,0.4)'
              : 'rgb(0,0,0,0.4)'
            key.style.borderWidth = '2px'
            key.style.display = 'block'
            key.style.height = '10px'
            key.style.marginRight = '5px'
            key.style.width = '10px'

            li.appendChild(key)

            const value = document.createElement('span')
            value.style.margin = '0px'
            value.style.padding = '10px'
            value.textContent = 'Listing Removed'

            li.appendChild(value)

            // Add to second row if it exists, otherwise first row
            if (row2) {
              row2.appendChild(li)
            } else {
              row1.appendChild(li)
            }
          }
        }
      }

      return {
        id: 'htmlLegend',
        afterUpdate: drawLegend,
        create: drawLegend
      }
    },
    labels(): string[] {
      const dates = this.history.flatMap((ut) => ut.history.map((_) => parseISO(_.date)))
      return eachDayOfInterval({ start: min(dates), end: max(dates) }).map((_) =>
        format(_, 'yyyy-MM-dd')
      )
    },
    chartData(): ChartData<'line'> {
      let gapOptions = {}
      if (this.spanGaps) {
        const skipped = (ctx: unknown, value: unknown) =>
          ctx.p0.skip || ctx.p1.skip ? value : undefined
        gapOptions = {
          segment: {
            borderColor: (ctx: unknown) =>
              skipped(ctx, this.$q.dark.isActive ? 'rgb(255,255,255,0.2)' : 'rgb(0,0,0,0.2)'),
            borderDash: (ctx: unknown) => skipped(ctx, [4, 4])
          },
          spanGaps: true
        }
      }
      const datasets: ChartData<'line'>['datasets'] = this.history.flatMap((h: Value) => {
        if (instanceOfLine(h)) {
          return [
            {
              label: h.label,
              data: this.labels.map((label) => {
                return h.history.find((_) => _.date === label)?.value || null
              }),
              fill: false,
              borderColor: h.color,
              backgroundColor: h.color,
              borderWidth: 3,
              pointRadius: false,
              stepped: this.spanGaps === true,
              ...gapOptions
            }
          ]
        } else {
          return [
            {
              label: h.labels[0],
              data: this.labels.map((label) => {
                return h.history.find((_) => _.date === label)?.value[0] || null
              }),
              fill: false,
              borderColor: h.color,
              backgroundColor: h.color,
              borderWidth: 1,
              pointRadius: false,
              stepped: this.spanGaps === true,
              ...gapOptions
            },
            {
              label: h.labels[1],
              data: this.labels.map((label) => {
                return h.history.find((_) => _.date === label)?.value[1] || null
              }),
              fill: '-1',
              borderColor: h.color,
              backgroundColor: h.color,
              borderWidth: 1,
              pointRadius: false,
              stepped: this.spanGaps === true,
              ...gapOptions
            }
          ]
        }
      })

      return {
        labels: this.labels,
        datasets
      }
    },
    chartOptions(): ChartOptions {
      const annotation1 =
        this.areaAnnotated && this.areaAnnotated.from > this.labels[0]
          ? {
              type: 'line',
              borderDash: [6, 6],
              borderWidth: 1,
              scaleID: 'x',
              value: this.areaAnnotated.from,
              label: {
                display: true,
                content: `From ${format(parseISO(this.areaAnnotated.from), 'M/d/yyyy')}`,
                position: 'start'
              }
            }
          : undefined

      const annotation2 =
        this.areaAnnotated && this.areaAnnotated.to < this.labels[this.labels.length - 1]
          ? {
              type: 'line',
              borderDash: [6, 6],
              borderWidth: 1,
              scaleID: 'x',
              value: this.areaAnnotated.to,
              label: {
                display: true,
                content: `To ${format(parseISO(this.areaAnnotated.to), 'M/d/yyyy')}`,
                position: 'start'
              }
            }
          : undefined

      const [minY, maxY] = yAxisLimits(
        this.history.flatMap((h: Value) => {
          if (instanceOfLine(h)) {
            return h.history.map((_) => _.value)
          } else {
            return h.history.flatMap((_) => _.value)
          }
        })
      )

      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_, clickedElements) => {
          if (clickedElements.length === 0 || !clickedElements[0].element?.$context) {
            return
          }
          const { index, datasetIndex } = clickedElements[0].element.$context
          this.clickedPosition(index, datasetIndex)
        },
        scales: {
          x: {
            ticks: {
              // For a category axis, the val is the index so the lookup via getLabelForValue is needed
              callback: function (val, index) {
                // Hide every 2nd tick label
                const label = this.getLabelForValue(val)
                return index % 3 === 0 ? format(parseISO(label), 'M/d/yyyy') : ''
              }
            }
          },
          y: {
            grace: '25%',
            ticks: {
              callback: this.formatFnc as (value: number | null) => string
            },
            min: minY,
            max: maxY
          }
        },
        interaction: {
          intersect: false
        },
        animation: false,
        options: {
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'day'
              },
              min: this.labels.length > 0 ? parseISO(this.labels[0]).valueOf() : undefined,
              max:
                this.labels.length > 0
                  ? parseISO(this.labels[this.labels.length - 1]).valueOf()
                  : undefined
            }
          },
          elements: {
            line: {
              borderWidth: 1
            },
            point: {
              radius: 0
            }
          }
        },
        plugins: {
          legend: {
            display: this.defaultLegend
          },
          htmlLegend: {
            containerID: this.defaultLegend ? undefined : this.legendKey,
            history: this.history
          },
          annotation: {
            annotations: { annotation1, annotation2 }
          },
          tooltip: {
            callbacks: {
              title: (context) => format(parseISO(context[0].label), 'M/d/yyyy'),
              label: (context) => {
                return ` ${context.dataset.label}`
              },
              afterLabel: (context) => {
                return `${this.label}: ${this.formatFnc(context.parsed.y, 1)}`
              }
            }
          }
        }
      }
    }
  },
  methods: {
    clickedPosition(position: number, dataset: number) {
      this.$emit('click', {
        position,
        dataset
      })
    }
  }
}
</script>
